<template>
    <!-- Fixed sidebar widget -->
    <div class="fixed right-0 top-1/2 z-40 hidden sm:flex -translate-y-1/2 flex-col items-end gap-1 noprint">
        <!-- Ansprechpartner suchen -->
        <div
            :class="hoveredItem === 'contact' ? 'shadow-xl' : ''"
            class="flex h-12 w-fit cursor-pointer items-center justify-end overflow-hidden rounded-l-md bg-primary text-white shadow-lg transition-shadow"
            type="button"
            @click="openContactPersonModal"
            @mouseenter="hoveredItem = 'contact'"
            @mouseleave="hoveredItem = null"
        >
            <div
                :class="hoveredItem === 'contact' ? 'max-w-[260px]' : 'max-w-0'"
                class="shrink-0 overflow-hidden transition-all duration-300"
            >
                <div class="whitespace-nowrap px-4 text-sm font-medium">
                    Ansprechpartner suchen
                </div>
            </div>

            <div class="flex h-12 w-12 shrink-0 items-center justify-center">
                <UIcon
                    class="h-5 w-5"
                    name="lucide:user-search"
                />
            </div>
        </div>

        <!-- Termin vereinbaren -->
        <div
            :class="hoveredItem === 'termin' ? 'shadow-xl' : ''"
            class="flex h-12 w-fit items-center justify-end overflow-hidden rounded-l-md bg-secondary text-white shadow-lg transition-shadow"
            type="button"
            @click="openContactPersonModal"
            @mouseenter="hoveredItem = 'termin'"
            @mouseleave="hoveredItem = null"
        >
            <div
                :class="hoveredItem === 'termin' ? 'max-w-[220px]' : 'max-w-0'"
                class="shrink-0 overflow-hidden transition-all duration-300"
            >
                <div class="whitespace-nowrap px-4 text-sm font-medium">
                    Termin vereinbaren
                </div>
            </div>

            <div class="flex h-12 w-12 shrink-0 items-center justify-center">
                <UIcon
                    class="h-5 w-5"
                    name="lucide:calendar"
                />
            </div>
        </div>

        <!-- Social media group: Instagram visible, others slide out on hover/touch -->
        <div
            class="flex flex-row items-end gap-1"
            @mouseenter="hoveredItem = 'social'"
            @mouseleave="hoveredItem = null"
            @touchstart.passive="handleSocialTouchStart"
            @touchend.passive="handleSocialTouchEnd"
        >
            <Transition name="social-slide-1">
                <NuxtLink
                    v-if="hoveredItem === 'social'"
                    class="flex h-12 w-12 items-center justify-center rounded-l-md text-white shadow-lg transition-shadow hover:shadow-xl"
                    style="background-color: #1877f2"
                    target="_blank"
                    title="Facebook"
                    to="https://www.facebook.com/sauerlandspielgeraete"
                >
                    <UIcon
                        class="h-5 w-5"
                        name="mdi:facebook"
                    />
                </NuxtLink>
            </Transition>

            <Transition name="social-slide-2">
                <NuxtLink
                    v-if="hoveredItem === 'social'"
                    class="flex h-12 w-12 items-center justify-center rounded-l-md text-white shadow-lg transition-shadow hover:shadow-xl"
                    style="background-color: #f00"
                    target="_blank"
                    title="YouTube"
                    to="https://www.youtube.com/channel/UCwLjh7t2vkx5LJkF0hVEl6g"
                >
                    <UIcon
                        class="h-5 w-5"
                        name="mdi:youtube"
                    />
                </NuxtLink>
            </Transition>

            <NuxtLink
                class="flex h-12 w-12 items-center justify-center rounded-l-md text-white shadow-lg transition-shadow hover:shadow-xl"
                style="background: linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)"
                target="_blank"
                title="Instagram"
                to="https://www.instagram.com/sauerland_spielgeraete/"
            >
                <UIcon
                    class="h-5 w-5"
                    name="lucide:instagram"
                />
            </NuxtLink>
        </div>
    </div>

    <UModal
        v-model:open="open"
        :close="{
      color: 'secondary',
      variant: 'solid',
      class: 'rounded-full'
    }"
        :ui="{
      title: 'pb-0!',
            content: 'w-[calc(100vw-2rem)] max-w-5xl',
      body: 'p-4 sm:p-6'
    }"
        scrollable
        title="Ansprechpartner suchen"
    >
        <template #body>
            <div class="space-y-4">
                <p>Geben Sie Ihre Postleitzahl ein, um den zuständigen Ansprechpartner zu finden.</p>
                <div class="flex items-center gap-2">
                    <UInput
                        v-model="zip"
                        autocomplete="postal-code"
                        class="flex-1"
                        inputmode="numeric"
                        maxlength="5"
                        placeholder="PLZ eingeben"
                        required
                        size="lg"
                        @keyup.enter="searchZip"
                    />

                    <UButton
                        :loading="searching"
                        aria-label="Postleitzahl suchen"
                        icon="lucide:search"
                        square
                        variant="subtle"
                        @click="searchZip"
                    />
                </div>

                <!-- Suchergebnis -->
                <div
                    v-if="location"
                    class="grid border-t border-neutral-400 pt-4 text-sm lg:grid-cols-2 lg:text-base"
                >
                    <div class="p-4">
                        <FormBooking
                            :to-email="location.contactPerson.email"
                            :zip="zip"
                            @sent="messageSent"
                        />
                    </div>

                    <div class="contact-persons space-y-8 py-2 pl-4">
                        <div class="grid grid-cols-1 gap-8">
                            <div
                                :class="location.contactPerson.image === null ? 'mt-4' : ''"
                                class="flex flex-col gap-4"
                            >
                                <div class="grid grid-cols-3 gap-8 items-center">
                                    <div class="col-span-2">
                                        <div class="text-xl font-bold uppercase text-primary lg:text-2xl">
                                            {{ location.contactPerson.name }}
                                        </div>

                                        <div class="italic">
                                            {{ location.contactPerson.description }}
                                        </div>
                                    </div>
                                    <NuxtPicture
                                        v-if="location.contactPerson.image"
                                        :alt="location.contactPerson.image.alternativeText"
                                        :img-attrs="{
                                          draggable: false,
                                          class: 'aspect-square rounded-full overflow-hidden object-cover justify-self-center'
                                        }"
                                        :src="'contact_persons/' + location.contactPerson.image.filename"
                                    />
                                </div>

                                <div class="flex flex-col gap-1">
                                    <div v-if="location.contactPerson.phone">
                                        <span class="font-bold">Telefon:</span>
                                        {{ location.contactPerson.phone }}
                                    </div>

                                    <div v-if="location.contactPerson.mobile">
                                        <span class="font-bold">Mobil:</span>
                                        {{ location.contactPerson.mobile }}
                                    </div>

                                    <div v-if="location.contactPerson.email">
                                        <span class="font-bold">E-Mail: </span>
                                        <a
                                            :href="'mailto:' + location.contactPerson.email"
                                            class="text-primary"
                                        >
                                            {{ location.contactPerson.email }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="location.substitute"
                            class="grid gap-8"
                        >
                            <div class="flex flex-col gap-4">
                                <div class="grid grid-cols-3 gap-8 items-center">
                                    <div class="col-span-2">
                                        <div class="text-xl font-bold uppercase text-secondary lg:text-2xl">
                                            {{ location.substitute.name }}
                                        </div>

                                        <div class="italic">
                                            {{ location.substitute.description }}
                                        </div>
                                    </div>
                                    <NuxtPicture
                                        v-if="location.substitute.image"
                                        :alt="location.substitute.image.alternativeText"
                                        :img-attrs="{
                                          draggable: false,
                                          class: 'rounded-full aspect-square object-cover justify-self-center'
                                        }"
                                        :src="'contact_persons/' + location.substitute.image.filename"
                                    />
                                </div>

                                <div class="flex flex-col gap-1">
                                    <div v-if="location.substitute.phone">
                                        <span class="font-bold">Telefon:</span>
                                        {{ location.substitute.phone }}
                                    </div>

                                    <div v-if="location.substitute.mobile">
                                        <span class="font-bold">Mobil:</span>
                                        {{ location.substitute.mobile }}
                                    </div>

                                    <div v-if="location.substitute.email">
                                        <span class="font-bold">E-Mail: </span>
                                        <a
                                            :href="'mailto:' + location.substitute.email"
                                            class="text-primary"
                                        >
                                            {{ location.substitute.email }}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else-if="!searching && zip.length === 5"
                    class="border-t border-neutral-400 pt-4"
                >
                    <p class="text-2xl font-bold uppercase text-secondary">
                        Keine Ansprechpartner für Ihre PLZ gefunden.
                    </p>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
import {until} from '@vueuse/core';

defineOptions({inheritAttrs: false});

interface Props {
    showModalContactPerson?: boolean;
    zip?: string;
}

const props = withDefaults(defineProps<Props>(), {
    showModalContactPerson: false,
    zip: '',
});

const emit = defineEmits<{
    'update:showModalContactPerson': [value: boolean]
}>();

type HoveredItem = 'contact' | 'termin' | 'social' | null

type ContactPersonImage = {
    alternativeText: string
    filename: string
}

type ContactPerson = {
    name: string
    description: string
    phone?: string | null
    mobile?: string | null
    email: string
    image?: ContactPersonImage | null
}

type LocationResult = {
    contactPerson: ContactPerson
    substitute?: ContactPerson | null
}

const toast = useToast();
const {gtag} = useGtag();

const hoveredItem = ref<HoveredItem>(null);
const open = ref(props.showModalContactPerson);
const zip = ref(props.zip);
const location = ref<LocationResult | null>(null);
const loading = ref(false);
const isSocialOpen = ref(false);

const searching = computed(() => loading.value);

function gaEventOpenContactPerson() {
    gtag('event', 'select_content', {
        content_type: 'Website Interaktion',
        content_id: 'Ansprechpartnersuche öffnen',
    });
}

function gaEventCloseContactPerson() {
    gtag('event', 'select_content', {
        content_type: 'Website Interaktion',
        content_id: 'Ansprechpartnersuche schließen',
    });
}

watch(
    () => props.showModalContactPerson,
    (value) => {
        open.value = value;
    },
);

watch(
    () => props.zip,
    (value) => {
        zip.value = value ?? '';
    },
);

watch(open, (value, previousValue) => {
    emit('update:showModalContactPerson', value);

    if (previousValue && !value) {
        gaEventCloseContactPerson();
    }
});

function openContactPersonModal() {
    open.value = true;
    gaEventOpenContactPerson();
}

onMounted(() => {
    // Close social icons when clicking outside on mobile
    const closeHandler = (event: PointerEvent) => {
        const target = event.target as HTMLElement | null;
        const socialGroup = document.querySelector('.social-group');

        if (socialGroup && !socialGroup.contains(target) && isSocialOpen.value) {
            isSocialOpen.value = false;
        }
    };

    document.addEventListener('pointerdown', closeHandler);

    onUnmounted(() => {
        document.removeEventListener('pointerdown', closeHandler);
    });
});

async function searchZip() {
    const z = zip.value.trim();

    if (z.length !== 5) {
        location.value = null;
        return;
    }

    loading.value = true;

    try {
        const res = await useAsyncGql({
            operation: 'findLocation',
            variables: {zip: z},
        });

        await until(res.pending)
            .toBe(false);

        const edges = res.data.value?.locations?.edges ?? [];
        location.value = edges.map((edge: any) => edge?.node)
            .filter(Boolean)[0] ?? null;
    } catch (error) {
        console.error('findLocation failed', error);
        location.value = null;
    } finally {
        loading.value = false;
    }
}

watch(zip, (value) => {
    const z = (value ?? '').trim();

    if (z.length === 5) {
        searchZip();
    }

    if (z.length < 5) {
        location.value = null;
    }
});

function messageSent(event: { success: boolean }) {
    toast.add({
        title: event.success ? 'Erfolgreich versendet' : 'Ein Fehler ist aufgetreten',
        description: event.success
            ? 'Ihre Nachricht wurde erfolgreich versendet. Wir melden uns in Kürze bei Ihnen.'
            : 'Beim Versenden der Nachricht ist ein Fehler aufgetreten. Versuchen Sie es später noch einmal.',
        color: event.success ? 'success' : 'error',
    });

    if (event.success) {
        open.value = false;
    }
}
</script>

<style scoped>
/* Facebook: enters first on desktop (0ms delay), leaves last */
.social-slide-1-enter-active {
    transform-origin: left;
    transition: opacity 250ms cubic-bezier(0.34, 1.56, 0.64, 1),
    margin 250ms cubic-bezier(0.34, 1.56, 0.64, 1),
    transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1);
    transition-delay: 0ms;
}

.social-slide-1-leave-active {
    transform-origin: left;
    transition: opacity 180ms ease-in,
    margin 180ms ease-in,
    transform 180ms ease-in;
    transition-delay: 60ms;
}

/* YouTube: enters second on desktop (60ms delay), leaves first */
.social-slide-2-enter-active {
    transform-origin: left;
    transition: opacity 250ms cubic-bezier(0.34, 1.56, 0.64, 1),
    margin 250ms cubic-bezier(0.34, 1.56, 0.64, 1),
    transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1);
    transition-delay: 60ms;
}

.social-slide-2-leave-active {
    transform-origin: left;
    transition: opacity 180ms ease-in,
    margin 180ms ease-in,
    transform 180ms ease-in;
    transition-delay: 0ms;
}

/* Collapse from left using negative margin and scale */
.social-slide-1-enter-from,
.social-slide-1-leave-to {
    margin-right: -48px;
    opacity: 0;
    transform: scaleX(0);
}

.social-slide-2-enter-from,
.social-slide-2-leave-to {
    margin-right: -48px;
    opacity: 0;
    transform: scaleX(0);
}

/* Mobile: simultaneous animation without stagger */
@media (hover: none) and (pointer: coarse) {
    .social-slide-1-enter-active,
    .social-slide-1-leave-active {
        transition-delay: 0ms !important;
    }

    .social-slide-2-enter-active,
    .social-slide-2-leave-active {
        transition-delay: 0ms !important;
    }
}
</style>
